import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBulletins, createBulletin, updateBulletin, acknowledgeBulletin, deleteBulletin } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Plus, Megaphone, AlertTriangle, Info, Bell, Pin, Check, Clock,
  ChevronLeft, User, Calendar, Trash2, Eye
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const PRIORITIES = [
  { value: 'urgent', label: 'Urgent', label_ar: 'عاجل', color: 'bg-red-600 text-white', icon: AlertTriangle },
  { value: 'high', label: 'High', label_ar: 'مرتفع', color: 'bg-orange-600 text-white', icon: Bell },
  { value: 'normal', label: 'Normal', label_ar: 'عادي', color: 'bg-sky-600 text-white', icon: Info },
  { value: 'info', label: 'Info', label_ar: 'معلومات', color: 'bg-slate-600 text-white', icon: Info },
];

const CATEGORIES = [
  { value: 'safety_alert', label: 'Safety Alert', label_ar: 'تنبيه السلامة' },
  { value: 'operational', label: 'Operational', label_ar: 'تشغيلي' },
  { value: 'regulatory', label: 'Regulatory', label_ar: 'تنظيمي' },
  { value: 'training', label: 'Training', label_ar: 'تدريب' },
  { value: 'maintenance', label: 'Maintenance', label_ar: 'صيانة' },
  { value: 'weather', label: 'Weather', label_ar: 'الطقس' },
  { value: 'general', label: 'General', label_ar: 'عام' },
];

const BulletinCard = ({ bulletin, onAcknowledge, onView, currentUserId, isRTL, language }) => {
  const priority = PRIORITIES.find(p => p.value === bulletin.priority) || PRIORITIES[2];
  const category = CATEGORIES.find(c => c.value === bulletin.category);
  const PriorityIcon = priority.icon;
  const isRead = bulletin.read_by?.includes(currentUserId);
  const isAcknowledged = bulletin.acknowledgments?.some(a => a.user_id === currentUserId);

  return (
    <Card 
      className={`bg-slate-900/60 border-slate-800 transition-all hover:border-slate-700 ${!isRead ? 'border-l-4 border-l-sky-500' : ''} ${bulletin.is_pinned ? 'ring-1 ring-yellow-500/30' : ''}`}
      data-testid={`bulletin-${bulletin.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Priority Icon */}
          <div className={`w-10 h-10 rounded-lg ${priority.color} flex items-center justify-center flex-shrink-0`}>
            <PriorityIcon className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {bulletin.is_pinned && (
                <Pin className="w-4 h-4 text-yellow-500" />
              )}
              <span className="font-mono text-xs text-slate-500">{bulletin.bulletin_number}</span>
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                {language === 'ar' ? category?.label_ar : category?.label}
              </Badge>
              <Badge className={`text-xs ${priority.color}`}>
                {language === 'ar' ? priority.label_ar : priority.label}
              </Badge>
              {!isRead && (
                <Badge className="bg-sky-600 text-white text-xs">New</Badge>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-white mb-1">
              {language === 'ar' && bulletin.title_ar ? bulletin.title_ar : bulletin.title}
            </h3>
            
            <p className="text-slate-400 text-sm line-clamp-2 mb-3">
              {language === 'ar' && bulletin.content_ar ? bulletin.content_ar : bulletin.content}
            </p>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {bulletin.created_by_name}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDistanceToNow(new Date(bulletin.created_at), { addSuffix: true })}
                </span>
                {bulletin.expires_at && (
                  <span className="flex items-center gap-1 text-orange-400">
                    <Calendar className="w-3 h-3" />
                    Expires {format(new Date(bulletin.expires_at), 'MMM d')}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onView(bulletin)}
                  className="text-slate-400 hover:text-white h-8"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                {!isAcknowledged && (
                  <Button
                    size="sm"
                    onClick={() => onAcknowledge(bulletin.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 h-8"
                    data-testid={`acknowledge-${bulletin.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Acknowledge
                  </Button>
                )}
                {isAcknowledged && (
                  <Badge className="bg-emerald-900/30 text-emerald-400 border border-emerald-900/50">
                    <Check className="w-3 h-3 mr-1" />
                    Acknowledged
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function BulletinsPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { language, isRTL } = useLanguage();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    content: '',
    content_ar: '',
    priority: 'normal',
    category: 'general',
    target_roles: [],
    expires_at: ''
  });

  useEffect(() => {
    fetchBulletins();
  }, [filterPriority]);

  const fetchBulletins = async () => {
    setLoading(true);
    try {
      const params = { active_only: true };
      if (filterPriority !== 'all') params.priority = filterPriority;
      const response = await getBulletins(api, params);
      setBulletins(response.data);
    } catch (error) {
      console.error('Failed to fetch bulletins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.expires_at) {
        payload.expires_at = new Date(payload.expires_at).toISOString();
      } else {
        delete payload.expires_at;
      }
      await createBulletin(api, payload);
      setShowCreate(false);
      setFormData({
        title: '', title_ar: '', content: '', content_ar: '',
        priority: 'normal', category: 'general', target_roles: [], expires_at: ''
      });
      fetchBulletins();
    } catch (error) {
      console.error('Failed to create bulletin:', error);
    }
  };

  const handleAcknowledge = async (bulletinId) => {
    try {
      await acknowledgeBulletin(api, bulletinId);
      fetchBulletins();
    } catch (error) {
      console.error('Failed to acknowledge bulletin:', error);
    }
  };

  const handleDelete = async (bulletinId) => {
    try {
      await deleteBulletin(api, bulletinId);
      setSelectedBulletin(null);
      fetchBulletins();
    } catch (error) {
      console.error('Failed to delete bulletin:', error);
    }
  };

  const canCreate = ['admin', 'safety_manager', 'safety_officer'].includes(user?.role);
  const canDelete = ['admin', 'safety_manager'].includes(user?.role);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight flex items-center gap-3" data-testid="bulletins-title">
            <Megaphone className="w-8 h-8 text-sky-400" />
            Safety Bulletin Board
          </h1>
          <p className="text-slate-400 mt-1">Urgent safety alerts and organizational announcements</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="new-bulletin-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Bulletin
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Filter by priority:</span>
            <div className="flex gap-2">
              <Button
                variant={filterPriority === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterPriority('all')}
                className={filterPriority === 'all' ? 'bg-sky-600' : 'border-slate-700 text-slate-300'}
              >
                All
              </Button>
              {PRIORITIES.map(p => (
                <Button
                  key={p.value}
                  variant={filterPriority === p.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterPriority(p.value)}
                  className={filterPriority === p.value ? p.color : 'border-slate-700 text-slate-300'}
                >
                  {language === 'ar' ? p.label_ar : p.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulletins List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : bulletins.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="py-12 text-center">
            <Megaphone className="w-16 h-16 mx-auto mb-4 text-slate-700" />
            <h3 className="text-xl font-semibold text-white mb-2">No Bulletins</h3>
            <p className="text-slate-400">
              {canCreate ? 'Create a new safety bulletin to alert your team.' : 'No active bulletins at this time.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bulletins.map(bulletin => (
            <BulletinCard
              key={bulletin.id}
              bulletin={bulletin}
              onAcknowledge={handleAcknowledge}
              onView={setSelectedBulletin}
              currentUserId={user?.id}
              isRTL={isRTL}
              language={language}
            />
          ))}
        </div>
      )}

      {/* Create Bulletin Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-sky-400" />
              Create Safety Bulletin
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Title (English) *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="Bulletin title"
                  data-testid="bulletin-title-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">العنوان (العربية)</Label>
                <Input
                  value={formData.title_ar}
                  onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="عنوان النشرة"
                  dir="rtl"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Content (English) *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                rows={4}
                className="bg-slate-950 border-slate-800 mt-1"
                placeholder="Bulletin content..."
                data-testid="bulletin-content-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">المحتوى (العربية)</Label>
              <Textarea
                value={formData.content_ar}
                onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
                rows={4}
                className="bg-slate-950 border-slate-800 mt-1"
                placeholder="محتوى النشرة..."
                dir="rtl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Priority *</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="bulletin-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${p.color.split(' ')[0]}`} />
                          {p.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Expiration Date (Optional)</Label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="bg-slate-950 border-slate-800 mt-1 w-48"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for no expiration</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-bulletin-btn">
                Publish Bulletin
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-slate-700 text-slate-300">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Bulletin Dialog */}
      <Dialog open={!!selectedBulletin} onOpenChange={() => setSelectedBulletin(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          {selectedBulletin && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500">{selectedBulletin.bulletin_number}</span>
                    {selectedBulletin.is_pinned && <Pin className="w-4 h-4 text-yellow-500" />}
                  </div>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(selectedBulletin.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
                <DialogTitle className="text-white text-xl">
                  {language === 'ar' && selectedBulletin.title_ar ? selectedBulletin.title_ar : selectedBulletin.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={PRIORITIES.find(p => p.value === selectedBulletin.priority)?.color}>
                    {PRIORITIES.find(p => p.value === selectedBulletin.priority)?.label}
                  </Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-400">
                    {CATEGORIES.find(c => c.value === selectedBulletin.category)?.label}
                  </Badge>
                </div>
                
                <div className="prose prose-invert max-w-none">
                  <p className="text-slate-300 whitespace-pre-wrap">
                    {language === 'ar' && selectedBulletin.content_ar ? selectedBulletin.content_ar : selectedBulletin.content}
                  </p>
                </div>

                <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t border-slate-800">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Posted by {selectedBulletin.created_by_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(selectedBulletin.created_at), 'PPp')}
                  </span>
                </div>

                {selectedBulletin.acknowledgments?.length > 0 && (
                  <div className="pt-4 border-t border-slate-800">
                    <h4 className="text-sm font-medium text-slate-400 mb-2">
                      Acknowledged by ({selectedBulletin.acknowledgments.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBulletin.acknowledgments.map((ack, idx) => (
                        <Badge key={idx} variant="outline" className="border-emerald-900/50 text-emerald-400">
                          <Check className="w-3 h-3 mr-1" />
                          {ack.user_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
